import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { documentApi } from "@/services/api/endpoints"; // Import a API

export interface Document {
  id: string;
  name: string;
  description: string;
  document_type: { id: string; name: string };
  process: { id: string; code: string; description: string } | null;
  user: { id: string; username: string };
  created_at: string;
  updated_at: string;
  type: string;
  session_id: string;
}

// Thunk assíncrono para buscar documentos
export const fetchDocuments = createAsyncThunk(
  "documents/fetchDocuments",
  async (_, { rejectWithValue }) => {
    try {
      const response = await documentApi.list();
      const docs = Array.isArray(response) ? response : response.results;
      if (!Array.isArray(docs)) {
        throw new Error("Formato de resposta da API de documentos inválido.");
      }
      return docs;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Falha ao buscar documentos.";
      return rejectWithValue(errorMessage);
    }
  },
);

export interface GeneratedDocInfo {
  generated_doc_id: string;
  status: string;
  download_url: string | null;
  error_message: string | null;
  updated_at: string;
}

interface DocumentsState {
  items: Document[];
  loading: boolean;
  error: string | null;
  generatedDocs: Record<string, GeneratedDocInfo>;
}

const initialState: DocumentsState = {
  items: [],
  loading: false,
  error: null,
  generatedDocs: {},
};

const documentsSlice = createSlice({
  name: "documents",
  initialState,
  reducers: {
    setDocuments: (state, action: PayloadAction<Document[]>) => {
      state.items = action.payload;
      state.loading = false;
      state.error = null;
    },
    clearDocuments: (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
    },
    documentGenerated: (state, action: PayloadAction<GeneratedDocInfo>) => {
      const info = action.payload;
      state.generatedDocs[info.generated_doc_id] = {
        ...info,
        updated_at: new Date().toISOString(),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDocuments.fulfilled,
        (state, action: PayloadAction<Document[]>) => {
          state.items = action.payload;
          state.loading = false;
        },
      )
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setDocuments, clearDocuments, documentGenerated } = documentsSlice.actions;

// Selectors
export const selectDocuments = (state: { documents: DocumentsState }) =>
  state.documents.items;
export const selectDocumentsLoading = (state: { documents: DocumentsState }) =>
  state.documents.loading;
export const selectDocumentsError = (state: { documents: DocumentsState }) =>
  state.documents.error;
export const selectGeneratedDoc = (state: { documents: DocumentsState }, docId: string) =>
  state.documents.generatedDocs[docId] ?? null;

export default documentsSlice.reducer;

export type { DocumentsState };
